import { createContext, useContext, useMemo, ReactNode, useCallback } from 'react';
import type { ExploreGroupListItem } from '../client/types.gen';
import { useAuth } from './AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { getGroups } from '../screens/home-search/api';

/**
 * `ExploreGroupListItem`, not `ExploreGroupResponse`: `GET /api/explore/groups`
 * returns the enriched list shape, and the Group Hub reads its members,
 * `saved_count`, `cover_photos` and `pending_votes_for_me` straight off it —
 * the same way the Home Hub reads its home off `HomesContext` rather than
 * fetching one. `getGroup()` carries none of those aggregates, so annotating
 * this as the plain response would have cost the hub a second request for data
 * it already holds.
 */
interface GroupsContextType {
    groups: ExploreGroupListItem[];
    loading: boolean;
    error: Error | null;
    refreshGroups: () => Promise<ExploreGroupListItem[]>;
}

const GroupsContext = createContext<GroupsContextType | undefined>(undefined);

export function GroupsProvider({ children }: { children: ReactNode }) {
    const { user, loading: authLoading } = useAuth();

    const {
        data: groupsData = [],
        isLoading,
        isError,
        error,
        refetch,
    } = useApiQuery(['explore', 'groups', user?.id], () => getGroups(), {
        enabled: !!user && !authLoading,
    });

    const refreshGroups = useCallback(async () => {
        const result = await refetch();
        return result.data ?? [];
    }, [refetch]);

    const value = useMemo(
        () => {
            const groups = user ? groupsData : [];
            const loading = authLoading || (!!user && isLoading);
            const resolvedError = isError
                ? error instanceof Error
                    ? error
                    : new Error('Failed to load groups')
                : null;

            return {
                groups,
                loading,
                error: resolvedError,
                refreshGroups,
            };
        },
        [user, groupsData, authLoading, isLoading, isError, error, refreshGroups]
    );

    return <GroupsContext.Provider value={value}>{children}</GroupsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGroups() {
    const context = useContext(GroupsContext);
    if (context === undefined) {
        throw new Error('useGroups must be used within a GroupsProvider');
    }
    return context;
}
